/**
 * RWA Royale — client entry point.
 *
 * The loop, in order, every frame:
 *
 *   1. FIXED STEP at TICK_HZ: sample one InputFrame per tick, run it through the
 *      predictor immediately, record it for reconciliation, queue it for send.
 *   2. RENDER at display rate: interpolate the predicted body across the partial
 *      tick, sample remote players 100ms in the past from the snapshot buffer,
 *      update VFX and HUD.
 *
 * The two rates are separate on purpose. The simulation is a fixed 30Hz on both
 * sides — a variable-dt client would produce a different position from the same
 * inputs and reconcile against the server forever. Rendering is uncapped.
 *
 * What this file never does: write a position, a health value, a cooldown, a
 * kill or a winner into anything the server reads. It sends buttons and it draws
 * what it is told.
 */

import * as THREE from 'three/webgpu';
import { TICK_MS, CHARACTERS, CRATE_HEALTH, abilityAnchor } from '@chaos/protocol';
import { PLAYER_HALF_HEIGHT, DASH_DURATION_MS, chaosPlayerScale } from '@chaos/sim';
import { createScene } from './SceneSetup.js';
import { renderFrame } from './PostProcessing.js';
import { CameraController, setCameraGroundLevel } from './CameraController.js';
import { initAimReticle, updateAimReticle } from './combat/AimReticle.js';
import { ArenaRenderer } from './ArenaRenderer.js';
import { recordChaosEvent, endMarket } from './arenas/market-state.js';
import { ZoneRenderer } from './ZoneRenderer.js';
import { getMapDef, getMapMeta, known as mapKnown } from './maps/index.js';
import { createCharacterModel, updateDashTrail, setCharacterShimmer } from './characters/CharacterModels.js';
import { preloadSkinned, hasSkinned, createSkinnedModel, SKINNED_IDS, skinnedLoadStatus } from './characters/SkinnedCharacter.js';
import { watchVisual, upgradeVisual } from './assets/VisualUpgrade.js';
import { InitialModelCohort } from './assets/InitialModelCohort.js';
import { markBoot, registerDiagnostics, installPerformanceDebug } from './performance/runtime.js';
import { updateSquashStretch, updateLeaderCrown, updateCastPose, applyCastAnticipation } from './PlayerVisuals.js';
import { initRemotePlayers, applyPoses } from './rendering/RemotePlayers.js';
import { attachHealthBar, updateHealthBar, attachAmmoPips, updateAmmoPips, updateCoinBadge } from './combat/HealthBars.js';
import { LootRenderer } from './combat/Loot.js';
import { initProjectiles, updateProjectiles, clearProjectiles, fireBasic, endBulletNear, bulletDirNear } from './combat/Projectiles.js';
import { initAbilityVFX, updateAbilityVFX, clearAbilityVFX, getVfxStats, playAbilityCast, playHitVFX, playChainArc, playEliminationVFX, playSpawnIn, playTravel, isTravelEffect } from './combat/AbilityVFX.js';
// Namespaced so cross-workstream helpers can be called behind `?.` guards while
// other workstreams are still landing (flashHitTarget, setDeathGrade).
import * as abilityVFX from './combat/AbilityVFX.js';
import * as postFX from './PostProcessing.js';
import { initTelegraphs, setTelegraphFloor, showTelegraph, updateTelegraphs, clearTelegraphs } from './combat/Telegraphs.js';
import { initHitFeedback, onDealtDamage, onTookDamage, onHealthChanged, onKillConfirmed, updateHitFeedback, isHitstopped, resetHitFeedback } from './combat/HitFeedback.js';
import { updateParticles, screenFlash, triggerCameraShake, clearAllParticles } from './vfx/ScreenEffects.js';
import { updateFx, clearFx, footstep as fxFootstep, landing as fxLanding, burst as fxBurst } from './vfx/fx.js';
import { createShaderWarmup, createSceneSweep } from './vfx/ShaderPrewarm.js';
import { updateEnvironmentEffects } from './EnvironmentEffects.js';
import { updateConveyorScrolls } from './SurfaceShaders.js';
import { CombatHUD } from './ui/CombatHUD.js';
import { KillFeed } from './ui/KillFeed.js';
import { DeathCam } from './ui/DeathCam.js';
import { RespawnPrompt } from './ui/RespawnPrompt.js';
import { Announcements, zoneLabel } from './ui/Announcements.js';
import { setupKeyboardInput, consumeActions, readIntent, releaseAll, keys, getAimSlot, isAiming } from './input/InputManager.js';
import { setStatusAura } from './combat/StatusAura.js';
import { setupMobileControls } from './input/MobileControls.js';
import { Connection } from './net/connection.js';
import { postMatchEnded, postRoomJoined } from './net/shellSession.js';
import { InputSender } from './net/inputSender.js';
import { Predictor } from './net/predict.js';
import { Reconciler } from './net/reconcile.js';
import { SnapshotBuffer } from './net/snapshotBuffer.js';
import { session, localPlayer, roster, remotePlayers, match, frame, cameraState } from './state.js';
import { shortAngleDist } from './math.js';
import { disposeBodyMaterials } from './rendering/RemotePlayers.js';
import { disposeLocalBody } from './rendering/LocalBody.js';

import { INPUT_FLUSH_MS, MAX_CATCHUP_TICKS, isDebug, isProbe, isTouch, readPotWei, touchProbe, useSkinned } from './config.js';
import {
  playDashSound,
  playCastSound,
  playJumpSound,
  playLandSound,
  playFootstep,
  playSpawnSound,
  playDeathSound,
  playCountdownBeep,
  playFightSound,
  playMatchStartHorn,
  playWinFanfare,
  startTensionDrone,
  unlockAudio,
} from './audio/SoundManager.js';

const boot = {
  el: document.getElementById('boot-overlay'),
  status: document.getElementById('boot-status'),
};

function setBootStatus(text, isError = false) {
  boot.status.textContent = text;
  boot.status.classList.toggle('error', isError);
}

const _scratchA = new THREE.Vector3();
const _scratchB = new THREE.Vector3();
/** A drawn round's heading, handed to the hit VFX so its shards fly back along the shot. */
const _hitDir = { x: 0, z: 0 };

/**
 * The phases in which the server steps the sim and accepts input — `ArenaRoom`
 * runs both through the same `updateSimulating` arm.
 *
 * A function rather than the expression written out at each site: this was
 * spelled out in three places, two of them meaning the same thing, and any
 * change that caught only one would sample input while freezing the movement
 * juice (or the reverse) without failing anything.
 */
/**
 * How long a countdown beep waits before it may land on top of the GET READY
 * banner. Announcements live ~900ms plus a fade, so anything sooner than this
 * stacks two lines in the same place.
 */
const GET_READY_CLEAR_MS = 600;

function isSimulatingPhase(phase) {
  return phase === 'drop' || phase === 'combat';
}

class Game {
  /**
   * @param {object} [connection] anything with the `Connection` surface. The gym
   *   (`src/gym/`) passes a LocalConnection that runs `@chaos/sim` in this tab
   *   instead of talking to a room; everything below is unaware of the swap.
   */
  constructor(connection = new Connection()) {
    this.connection = connection;
    this.sender = new InputSender(this.connection);
    this.predictor = null;
    this.reconciler = null;
    this.snapshots = new SnapshotBuffer();
    /** Ticks of local basic casts drawn ahead of the server's echo. See `_onPredictedEvents`. */
    this._predictedBasics = [];
    /** performance.now() of your own travels drawn from prediction, by kind. See `_onTravel`. */
    this._predictedTravels = [];

    this.accumulatorMs = 0;
    this.lastFrameTime = 0;
    this.lastFlush = 0;
    /** Server tick we believe we are on. Corrected by every snapshot. */
    this.tick = 0;
    this.ready = false;
    this._requestedModels = new Set();
    this._initialModelCohort = new InitialModelCohort({
      ids: useSkinned ? SKINNED_IDS : [],
      onSettled: result => markBoot(result.ready ? 'optionalModelsReady' : 'optionalModelsSettled', result),
    });
    this.dead = false;
    this._lastHealth = null;
    /** Pending 3-2-1 beeps, so a match that ends early cannot shout into it. */
    this._dropTimers = [];
    /** Input pipeline started. Once per match, on whichever simulating phase
     *  we see first — `drop` normally, `combat` for a reconnect mid-match. */
    this._pipelineStarted = false;
    /** The drop ceremony is once per match too, and on a different trigger. */
    this._dropCeremonyPlayed = false;

    // Presentation edge-detection state. All of it reads the predictor or the
    // snapshot mirror — none of it feeds back into the sim.
    this._wasGrounded = true;
    this._peakFallVy = 0;
    this._stepClock = 0;
    this._stepAlt = false;
    this._minePhase = 'alive';
    this._drone = null;

    // Passed to applyPoses every frame; built once so the render loop does not
    // allocate a closure per frame.
    this._nameFor = (id) => roster.get(id)?.displayName ?? 'Player';
  }

  async boot() {
    setBootStatus('starting renderer…');
    const { scene, camera, renderer } = await createScene();
    markBoot('rendererReady');
    if (isProbe) {
      // Dynamic, so 135 lines of dev measurement stay out of the player bundle:
      // the flag gated INSTALLATION while a static import shipped the module to
      // everyone regardless.
      const probe = await import('./tools/probe.js');
      probe.installProbe(scene);
      probe.installClipProbe();
    }
    this.scene = scene;
    this.camera = camera;
    this.renderer = renderer;
    installPerformanceDebug();
    registerDiagnostics('models', skinnedLoadStatus);
    registerDiagnostics('vfx', getVfxStats);
    // In combat too. The old `session.phase !== 'combat'` gate meant a
    // practice room — always in combat — never warmed a single shader, so
    // every first cast there paid its compile mid-fight (60–200 ms frames:
    // the founder's "super laggy"). DeferredWarmup already runs one job per
    // idle callback and only with ≥ 8 ms of idle budget, which is the pacing
    // a fight can afford; a compile on an idle slice beats one on the frame
    // the ability fires.
    this.shaderWarmup = createShaderWarmup(renderer, scene, camera, {
      canRun: () => this.ready && !document.hidden,
    });
    registerDiagnostics('shaderWarmup', () => this.shaderWarmup.stats());
    // Every render object in a freshly built arena, built behind the boot
    // overlay rather than the first time the camera turns onto it.
    this.sceneSweep = createSceneSweep(scene);
    registerDiagnostics('sceneSweep', () => this.sceneSweep.stats());
    registerDiagnostics('bodies', () => ({
      local: localPlayer.mesh ? { characterId: localPlayer.mesh.userData.characterId, skinned: !!localPlayer.mesh.userData.skinned, dead: !!localPlayer.mesh.userData.driver?.dead } : null,
      remote: Array.from(remotePlayers.values(), mesh => ({ characterId: mesh.userData.characterId, skinned: !!mesh.userData.skinned, dead: !!mesh.userData.driver?.dead })),
    }));

    this.cameraController = new CameraController(camera, renderer);
    this.cameraController.initDesktopEvents();

    // With the renderer and camera so every market material is compiled at
    // map load, against the real pass target, never on first activation.
    this.arena = new ArenaRenderer(scene, { renderer, camera });
    this.zone = new ZoneRenderer(scene);
    this.loot = new LootRenderer(scene);
    initRemotePlayers(scene);
    initAbilityVFX(scene);
    initTelegraphs(scene, 0);
    initAimReticle(scene, renderer);
    initProjectiles(scene, (id, out) => this._positionOf(id, out));
    initHitFeedback(camera, this.cameraController);

    this.hud = new CombatHUD();
    this.killFeed = new KillFeed('');
    this.deathCam = new DeathCam(this.cameraController);
    // Practice rooms only. `connection.willRespawn()` decides which of the two
    // a death gets; this one never appears in a wagered match.
    this.respawnPrompt = new RespawnPrompt({
      onRespawn: () => {
        this.respawnPrompt.hide();
        this.connection.respawn?.();
      },
    });
    this.announcements = new Announcements();

    match.potWei = readPotWei();
    this.hud.setPot(match.potWei);
    this.deathCam.setPot(match.potWei);

    setupKeyboardInput({ onFirstInput: unlockAudio });
    setupMobileControls({
      rendererDomElement: renderer.domElement,
      cameraController: this.cameraController,
    });

    this._wireNetwork();

    setBootStatus('joining match…');
    try {
      await this.connection.join();
      markBoot('roomJoined');
    } catch (err) {
      setBootStatus(String(err.message || err), true);
      throw err;
    }

    this.lastFrameTime = performance.now();
    renderer.setAnimationLoop(() => this.tickFrame());
  }

  // ── Network ─────────────────────────────────────────────────

  _wireNetwork() {
    this.connection
      .on('world_init', (msg) => this._onWorldInit(msg))
      .on('snapshot', (msg) => this._onSnapshot(msg))
      .on('events', (events) => this._onEvents(events))
      .on('state', (state) => this._onState(state))
      .on('result', (msg) => this._onResult(msg))
      .on('leave', (code) => this._onLeave(code))
      .on('character_changed', (msg) => this._onCharacterChanged(msg))
      .on('error', ({ message }) => setBootStatus(`server error: ${message}`, true));
  }

  /**
   * Remove and free the current local body, if any.
   *
   * Materials and their textures are disposed explicitly: three.js does not
   * release GPU resources when a node leaves the scene graph, so dropping the
   * reference alone leaks a program and a texture per swap.
   */
  _disposeLocalBody() {
    // Scene, driver, status aura (pooled shapes back to the pool), materials.
    disposeLocalBody(localPlayer.mesh);
    localPlayer.mesh = null;
  }

  /**
   * The scrim swapped this player's character underneath them.
   *
   * Only a practice room sends this — in a wagered match the character is on
   * the paid entry row and cannot change. Rebuilds the one thing `world_init`
   * built from it: the body, its health, the HUD and the ability bar.
   */
  _onCharacterChanged(msg) {
    const id = msg?.characterId;
    if (!id || !CHARACTERS[id] || id === session.characterId) return;
    session.characterId = id;

    this._disposeLocalBody();
    localPlayer.mesh = watchVisual(createCharacterModel(id, session.userId));
    localPlayer.maxHealth = CHARACTERS[id].maxHealth;
    localPlayer.health = localPlayer.maxHealth;
    this.scene.add(localPlayer.mesh);
    this.hud.setCharacter(id);
    this._requestModels([id]);
  }

  _onWorldInit(msg) {
    // A replacement world must not inherit strikes or leases from its predecessor.
    clearAbilityVFX();
    clearTelegraphs();
    clearAllParticles();
    clearFx();
    clearProjectiles();
    this.loot.clear();
    this._predictedBasics.length = 0;
    this.hud.setPractice(msg.casual === true);
    if (typeof msg.potWei === 'string' && /^\d+$/.test(msg.potWei)) {
      match.potWei = msg.potWei;
      this.hud.setPot(match.potWei);
      this.deathCam.setPot(match.potWei);
    }
    session.matchId = msg.matchId;
    session.mapId = msg.mapId;
    session.userId = msg.you.userId;
    session.characterId = msg.you.characterId;
    session.seat = msg.you.seat;
    session.tickHz = msg.tickHz;
    session.totalTicks = msg.totalTicks;
    session.dropEndTick = msg.dropEndTick ?? 0;
    session.arenaRadius = msg.arenaRadius;
    session.seedCommitment = msg.seedCommitment;
    session.connected = true;
    if(!msg.casual && !msg.scrim)postRoomJoined(msg.matchId);

    const map = getMapDef(msg.mapId, { arenaRadius: msg.arenaRadius });
    // The camera's floor clamp needs to know where the ground is. A map with no
    // floor at all ("none") keeps 0, which is where its platforms sit anyway.
    this._groundY = map.floor.kind === 'none' ? 0 : map.floor.y;
    setCameraGroundLevel(this._groundY);
    if (!mapKnown(msg.mapId)) {
      // Prediction runs against this geometry. If it is not the server's, every
      // ledge mispredicts — worth one loud line rather than a silent rubber-band.
      console.warn(
        `[map] "${msg.mapId}" has no shared definition yet; predicting against the graybox bowl`
      );
    }
    this.map = map;
    this.arena.build(map);
    this.zone.setFloorY(map.floor.kind === 'none' ? 0 : map.floor.y);
    setTelegraphFloor(map.floor.kind === 'none' ? 0 : map.floor.y);

    this.predictor = new Predictor({
      playerId: session.userId,
      characterId: session.characterId,
      map,
      seed: msg.seedCommitment || 'client',
    });
    this.reconciler = new Reconciler(this.predictor, this.sender);

    const spawn = (msg.spawns ?? []).find((s) => s.seat === msg.you.seat);
    if (spawn) this.predictor.setSpawn(spawn.x, spawn.y, spawn.z, spawn.yaw ?? 0);

    // The local body. Built from the character on the PAID ENTRY ROW, which the
    // server put in `world_init` — the client never picked it. The seed is the
    // stable user id, so this body is YOUR body, tonight and every night.
    //
    // DISPOSED FIRST, because `world_init` is no longer once-per-match. The
    // offline gym re-emits it on each character swap (`LocalConnection
    // .setCharacter`), and without this each swap ADDED a body and kept the
    // previous one — two characters standing inside each other, which is what
    // the picker was showing. A message that can arrive twice has to be
    // idempotent; requiring every caller to clean up first is how the second
    // caller gets it wrong, and there was already a caller doing it by hand.
    this._disposeLocalBody();
    localPlayer.mesh = watchVisual(createCharacterModel(session.characterId, session.userId));
    localPlayer.maxHealth = (CHARACTERS[session.characterId] ?? CHARACTERS.volt).maxHealth;
    localPlayer.health = localPlayer.maxHealth;
    this.scene.add(localPlayer.mesh);

    this.hud.setCharacter(session.characterId);
    this.killFeed.setLocalUserId(session.userId);

    const meta = getMapMeta(msg.mapId);
    setBootStatus(`${meta.name} — waiting for the drop`);
    this.ready = true;
    // The arena is built: draw all of it once, culling off, so its render
    // objects exist before the first camera move.
    this.sceneSweep.arm();
    markBoot('worldReady');
    this._initialModelCohort.setLocal(session.characterId);
    // A practice room holds this player's spawn shield until `ready`, so there
    // it waits for the arena art and the shader warm-up, which on a phone is
    // the long part, not just for world_init. Capped so a stuck load cannot
    // hold the shield forever (the server caps it too).
    const readyToken = (this._readyToken = {});
    if (this.connection.readyAfterLoad) {
      // Same world AND same room: a reconnect mid-load gets its own world_init
      // and its own ready; this one must not land on the new room first.
      const room = this.connection.room;
      Promise.race([this.arena.prewarmed.catch(() => {}), new Promise((r) => setTimeout(r, 15000))])
        .then(() => {
          if (this._readyToken === readyToken && this.connection.room === room) this.connection.sendReady(msg.revision);
        });
    } else {
      this.connection.sendReady();
    }
    this._requestModels([session.characterId, ...Array.from(roster.values(), p => p.characterId)]);
    // The other half of the race described on `_maybeStartPipeline`: if the
    // phase change already went past while `ready` was false, this is the call
    // that actually starts the pipeline.
    this._maybeStartPipeline(session.phase);
    setTimeout(() => boot.el.classList.add('hidden'), 400);
  }

  /** Optional art never participates in join/world/prediction readiness. */
  _requestModels(ids) {
    if (!this.ready) return;
    if (!useSkinned) {
      this._initialModelCohort.update({});
      return;
    }
    const needed = [...new Set(ids)].filter(id => SKINNED_IDS.includes(id) && !this._requestedModels.has(id));
    if (!needed.length) {
      this._initialModelCohort.update(skinnedLoadStatus());
      return;
    }
    for (const id of needed) this._requestedModels.add(id);
    void preloadSkinned(needed).then(loaded => {
      const states = skinnedLoadStatus();
      this._initialModelCohort.update(states);
      if (loaded.length < needed.length) console.warn('[skinned] optional models unavailable; keeping procedural bodies', states);
    });
  }

  _upgradeLocalVisual() {
    const mesh = localPlayer.mesh;
    if (!useSkinned || !mesh || this.dead || !hasSkinned(session.characterId)) return;
    upgradeVisual(mesh, () => createSkinnedModel(session.characterId), { dispose: disposeBodyMaterials });
  }

  _onSnapshot(snapshot) {
    if (!this.predictor) return;
    this.snapshots.push(snapshot);

    match.tick = snapshot.tick;
    // First snapshot of the drop: both the end tick and the current tick are
    // known here, and nowhere earlier.
    this._maybePlayDropCeremony();
    match.aliveCount = snapshot.aliveCount;
    match.timeRemainingMs = snapshot.timeRemainingMs;
    match.zone = snapshot.zone;
    this.loot.update(snapshot.loot);

    // The server's clock wins. Ours only fills the gaps between snapshots.
    this.tick = snapshot.tick;

    const mine = snapshot.players.find((p) => p.id === session.userId);
    if (!mine) return;

    this.reconciler.apply(snapshot, mine);

    localPlayer.health = mine.health;
    localPlayer.ultCharge = mine.ultCharge;
    localPlayer.phase = mine.phase;
    localPlayer.cooldowns = mine.cooldowns;
    localPlayer.effects = mine.effects;
    localPlayer.coins = mine.coins ?? 0;

    if (this._lastHealth !== null && mine.health < this._lastHealth && !this._hitThisSnapshot) {
      // Damage with no `hit` event behind it: the zone, or a damage-over-time.
      // The zone reports 0.4 HP per snapshot at 6dps, so showing each delta
      // would be a stream of "0"s — it accumulates until it is worth a number.
      this._driftDamage = (this._driftDamage ?? 0) + (this._lastHealth - mine.health);
      if (this._driftDamage >= 4) {
        onTookDamage(this._driftDamage, null, localPlayer.renderPos);
        this._driftDamage = 0;
      }
    }
    this._hitThisSnapshot = false;
    this._lastHealth = mine.health;
    onHealthChanged(mine.health, localPlayer.maxHealth);

    // A wagered match never resurrects, but the gym and god mode do. Bodies
    // burst into existence instead of just existing — and a scattered toy is
    // put back together from the same seed-identical pieces.
    if (this._minePhase === 'dead' && mine.phase === 'alive') {
      localPlayer.mesh?.userData.driver?.revive();
      // The beam-in, in your own colour — the same one an opponent's respawn draws.
      playSpawnIn(_scratchA.set(mine.x, mine.y, mine.z), session.characterId);
      playSpawnSound();
      // Back on your feet: drop the prompt, undo the hide, and clear the flag.
      // `this.dead` is set on death and, before this, was never cleared —
      // harmless in a wagered match where death is final, but in a practice
      // room it left a revived player permanently flagged dead, which the
      // render path below reads when it decides whether to draw a corpse.
      this.respawnPrompt.hide();
      // And the death cam, for the same reason. It only opens in a room that
      // does NOT respawn you, so this is the god-mode path: revived after the
      // screen was already up. Without it the ELIMINATED overlay stays on top
      // of a living player and the camera stays where it was left — `hide()`
      // reattaches it. It was written for this and never called.
      this.deathCam.hide();
      this.dead = false;
      if (localPlayer.mesh) localPlayer.mesh.visible = true;
    }
    this._minePhase = mine.phase;

    this.hud.update(mine, snapshot.tick, match);

    if (mine.phase === 'dead' && !this.dead) this._enterDeathCam();
  }

  _onEvents(events) {
    for (const ev of events) {
      switch (ev.t) {
        case 'ability_cast':
          this._onAbilityCast(ev);
          break;
        case 'hit':
          this._onHit(ev);
          break;
        case 'effect_applied':
          if (isTravelEffect(ev.effect)) this._onTravel(ev, false);
          break;
        case 'eliminated':
          this._onEliminated(ev);
          break;
        case 'zone_shrink':
          this.announcements.push('zone', zoneLabel(ev.targetRadius), ev.warnMs);
          this._zoneWarnUntil = performance.now() + ev.warnMs;
          break;
        case 'chaos_event':
          this.predictor?.applyChaosEvent(ev);
          // Ambient kinds never enter the sim's ledger (by design: weather
          // must not touch the checksum), so the presentation keeps its own.
          recordChaosEvent(ev, this.predictor?.world ?? null);
          // The payload rides along for the pool's own description line
          // and the duration under the label.
          this.announcements.push('chaos', ev.label, ev.warnMs, ev.mutation ?? null);
          // Reads --event rather than repeating its hex, so the flash and the
          // banner cannot drift. Was v1's violet #b46cff.
          screenFlash('var(--event, #e8f2f4)', 320, 0.2);
          break;
        case 'match_end':
          this._onMatchEnd(ev);
          break;
        case 'loot_spawn':
          this.loot.onSpawn(ev);
          break;
        case 'pickup':
          this.loot.onPickup(ev, session.userId, this._positionOf(ev.playerId, _scratchA));
          break;
        default:
          break;
      }
    }
  }

  /**
   * Events the LOCAL sim produced this tick. Only one thing is drawn from
   * them: your own basic attack, the same frame the button went down.
   *
   * The reference game feels responsive because the shooter never waits for
   * the server to see their own bullet, and every basic here is a pure
   * function of your own input and the roster — the server will produce the
   * identical cast one round-trip later. That echo is recognised and skipped
   * in `_onAbilityCast`, so nothing is drawn twice. Nothing else is taken from
   * these events: no hit, no damage, no kill — the local world has nobody in
   * it to hit, and a predicted hit is a lie either way.
   */
  _onPredictedEvents(events) {
    for (const ev of events) {
      // Your own roll, dash or leap starts on your screen the tick it starts
      // in your prediction, like your basic does.
      if (ev.t === 'effect_applied' && ev.playerId === session.userId && isTravelEffect(ev.effect)) {
        this._onTravel(ev, true);
        continue;
      }
      if (ev.t !== 'ability_cast' || ev.playerId !== session.userId) continue;
      const ability = abilityById(session.characterId, ev.abilityId);
      if (ability?.slot !== 'basic') continue;
      this._onAbilityCast(ev, true);
    }
  }

  /**
   * A travel (roll, dash, ride, leap) began on a body: its trail, smears and
   * arrival. Your own are drawn from prediction and the server's echo of each
   * is skipped — unless nothing was predicted, which is a ride someone else
   * put you on: that one only the server knows about.
   */
  _onTravel(ev, predicted) {
    const mine = ev.playerId === session.userId;
    if (mine) {
      const now = performance.now();
      // Anything older than a generous round trip is not this echo's.
      this._predictedTravels = this._predictedTravels.filter((t) => now - t.at < 1500);
      if (predicted) {
        this._predictedTravels.push({ kind: ev.effect, at: now });
      } else {
        const i = this._predictedTravels.findIndex((t) => t.kind === ev.effect);
        if (i >= 0) {
          this._predictedTravels.splice(i, 1);
          return;
        }
      }
    }
    const characterId = mine ? session.characterId : roster.get(ev.playerId)?.characterId;
    playTravel(ev.effect, characterId ?? session.characterId, ev.playerId, ev.durationMs);
  }

  /**
   * @param {object} ev the cast event, from the server or (predicted) from the local sim
   * @param {boolean} predicted true when this is the local sim's own event
   */
  _onAbilityCast(ev, predicted = false) {
    const row = roster.get(ev.playerId);
    const characterId = row?.characterId ?? session.characterId;
    const ability = abilityById(characterId, ev.abilityId);

    // Your own basic is drawn from the PREDICTED event (above). The server's
    // echo of the same press arrives a round-trip later and must not draw it
    // again; the queue pairs each echo with the prediction it confirms.
    if (ability?.slot === 'basic' && ev.playerId === session.userId) {
      if (predicted) {
        this._predictedBasics.push(ev.tick);
        if (this._predictedBasics.length > 8) this._predictedBasics.shift();
      } else if (this._predictedBasics.length > 0) {
        this._predictedBasics.shift();
        return;
      }
    }

    // ONE origin per cast, chosen by the anchor the sim resolves with — VFX and
    // telegraph both receive it, so the three (resolve, decal, effect) can
    // never start from different places. WORLD casts live at the event's own
    // x/z, the frozen origin the sim resolves them from; BODY casts live on the
    // caster's rendered transform, which the sim's live-position resolve maps
    // to on this screen (remote bodies render ~100ms behind — the effect
    // belongs on the body as DRAWN, not on a snapshot value).
    const live = this._positionOf(ev.playerId, _scratchA);
    const worldAnchored = ability ? abilityAnchor(ability) === 'world' : false;
    const origin = live ?? _scratchA.set(ev.x, PLAYER_HALF_HEIGHT, ev.z);
    if (worldAnchored) origin.set(ev.x, origin.y, ev.z);

    playAbilityCast(ev.abilityId, characterId, ev.playerId, origin, ev.yaw);

    // The telegraph is the reactable half of the wind-up. It is drawn for
    // everyone, including yourself — knowing your own commit window is how you
    // learn to stop cancelling into it.
    showTelegraph({
      abilityId: ev.abilityId,
      characterId,
      playerId: ev.playerId,
      x: origin.x,
      y: origin.y - PLAYER_HALF_HEIGHT,
      z: origin.z,
      yaw: ev.yaw,
    });

    // Cast audio is the main warning for something being wound up behind you,
    // but eight simultaneous casters would be mush. Anything past ~28 units is
    // not a threat to you yet and stays silent.
    if (ability) {
      const mine = ev.playerId === session.userId;
      if (mine || origin.distanceTo(localPlayer.renderPos) < 28) {
        playCastSound(ability.windupMs, mine ? 300 : 220);
      }
    }

    // The basic's own presentation: bullets replayed from the cast (ended by
    // the server's `hit`), or the melee swing through the VFX seam. Walls are
    // traced against the predictor's world, which carries the shared map.
    if (ability?.slot === 'basic') {
      fireBasic({
        ownerId: ev.playerId,
        characterId,
        origin,
        yaw: ev.yaw,
        atMs: performance.now(),
        world: this.predictor?.world ?? null,
      });
    }

    const mesh = ev.playerId === session.userId ? localPlayer.mesh : remotePlayers.get(ev.playerId);
    if (mesh && ability) {
      applyCastAnticipation(mesh, ability.windupMs);
      // The cast layer animates the ability's SHAPE (`hit.kind` picks the
      // archetype) on the ability's OWN clock: the chamber stretches to the
      // real wind-up, so the coil on the body IS the reactable telegraph, and
      // the strike lands when the sim resolves it.
      mesh.userData.driver?.cast(performance.now(), ability);
    }

    if (ev.abilityId === 'dash') {
      playDashSound();
      // The dash pose: the whole body commits into the travel for the sim's
      // exact i-frame window — on every body, yours and theirs.
      mesh?.userData.driver?.dash(performance.now(), DASH_DURATION_MS);
      // The FOV punch is the whole reason a dash reads as fast. Only your own.
      if (ev.playerId === session.userId) this.cameraController.punchFov(6);
    }
  }

  _onHit(ev) {
    const point = _scratchA.set(ev.x, ev.y, ev.z);
    const attacker = roster.get(ev.attackerId);
    const target = roster.get(ev.targetId);
    const heavy = ev.damage >= 25;

    // The drawn round's heading, read BEFORE it is ended, so the impact's
    // shards fly back from the body the way the round came in.
    const dir = ev.abilityId === 'basic' ? bulletDirNear(ev.attackerId, point, _hitDir) : null;
    playHitVFX(ev.abilityId, attacker?.characterId ?? 'volt', point, heavy, ev.attackerId, dir);
    // The point too: a live shield ripples from where it was struck.
    abilityVFX.flashHitTarget?.(ev.targetId, point);
    // The server says a bullet landed here: end the drawn one nearest to it.
    // Melee basics have no drawn bullet, and this finds nothing for them.
    if (ev.abilityId === 'basic') endBulletNear(ev.attackerId, point);

    // A crate: it knocks about and shows your number, and that is all — there
    // is no body to flinch and nobody took damage.
    if (this.loot.onHit(ev.targetId)) {
      if (ev.attackerId === session.userId) onDealtDamage(point, ev.damage, CRATE_HEALTH, ev.abilityId === 'basic');
      return;
    }

    // The flinch pose pairs with the white-out flash above — spine arched,
    // arms flung wide, on whoever took the hit (yourself included).
    const flinched = ev.targetId === session.userId ? localPlayer.mesh : remotePlayers.get(ev.targetId);
    flinched?.userData.driver?.hit(performance.now());

    // Chain Lightning resolves as one `hit` per link, and the arc between the
    // caster and each victim is the only thing that makes the chain legible —
    // three separate impact puffs read as three unrelated hits.
    if (ev.abilityId === 'chain_lightning') {
      const source = this._positionOf(ev.attackerId, _scratchB);
      if (source) playChainArc(source, point, attacker?.characterId ?? 'volt');
    }

    if (ev.attackerId === session.userId) {
      onDealtDamage(point, ev.damage, target?.maxHealth ?? 100, ev.abilityId === 'basic');
    }
    if (ev.targetId === session.userId) {
      this._hitThisSnapshot = true;
      const from = this._positionOf(ev.attackerId, _scratchB);
      onTookDamage(ev.damage, from, localPlayer.renderPos, ev.abilityId === 'basic');
    }
  }

  _onEliminated(ev) {
    this.killFeed.onElimination(ev, (id) => {
      const row = roster.get(id);
      return row ? { name: row.displayName, characterId: row.characterId } : null;
    });

    const at = this._positionOf(ev.victimId, _scratchA);
    if (at) playEliminationVFX(at, roster.get(ev.victimId)?.characterId ?? 'volt');

    if (ev.killerId === session.userId) onKillConfirmed();
    if (ev.victimId === session.userId) {
      this._pendingDeath = {
        killerId: ev.killerId,
        killerName: roster.get(ev.killerId)?.displayName ?? null,
        placement: ev.placement,
      };
    }
  }

  _onMatchEnd(ev) {
    this._clearDropTimers();
    match.ended = true;
    match.winnerId = ev.winnerId;
    match.endReason = ev.reason;
    const winner = ev.winnerId ? roster.get(ev.winnerId) : null;
    this.deathCam.showResult({
      winnerName: winner?.displayName ?? null,
      isWinner: ev.winnerId === session.userId,
    });
    resetHitFeedback();
    // The fight's debris must not survive onto the result screen. Abilities can
    // outlive the kill that ended the match by seconds — a ground zone runs 12s
    // — so without this the winner's fanfare plays over the loser's last cast.
    clearAbilityVFX();
    clearTelegraphs();
    clearAllParticles();
    clearFx();
    clearProjectiles();
    this._predictedBasics.length = 0;
    // The predictor's tick freezes here, so a live tremor or fog would play
    // over the results screen for as long as it is up.
    endMarket(this.predictor?.world ?? null);
    this._drone?.stop();
    this._drone = null;

    if (ev.winnerId === session.userId) {
      playWinFanfare();
      screenFlash('#ffc531', 600);
      fxBurst(localPlayer.renderPos, 0xffc531, { count: 40, speed: 10 });
    }
  }

  /** Colyseus Schema — the coarse channel. Never positional. */
  _onState(state) {
    if (state.phase !== session.phase) this._onPhaseChange(state.phase);
    session.phase = state.phase;
    state.players.forEach((p, userId) => {
      roster.set(userId, {
        userId,
        displayName: p.displayName || userId.slice(0, 8),
        characterId: p.characterId,
        // Carried through because it is the only stable ORDER a roster has —
        // a Map iterates in insertion order, which is join order, so a list
        // sorted by anything else jumps around as people come and go.
        seat: p.seat,
        health: p.health,
        maxHealth: p.maxHealth,
        phase: p.phase,
        placement: p.placement,
        kills: p.kills,
        damageDealt: p.damageDealt,
        connected: p.connected,
      });
    });
    const rosterIds = Array.from(roster.values(), p => p.characterId);
    this._initialModelCohort.setRoster(rosterIds);
    this._requestModels(rosterIds);
  }

  /** Dispatch only. Each of the three things a phase change means owns itself. */
  _onPhaseChange(phase) {
    // `session.phase` is still the OLD value here — `_onState` assigns it after
    // this returns — so every check below reads the argument, not the mirror.
    this._maybeStartPipeline(phase);
    // Every exit from the drop, not just the one into combat. The comment here
    // used to claim "anything pending belongs to a drop that is over" while
    // only clearing on the `combat` branch, so a drop that ended any other way
    // left three beeps queued over whatever replaced it.
    if (phase !== 'drop') this._clearDropTimers();
    if (phase === 'combat') this._playFightStart();
  }

  /**
   * Start sampling input. Once per match, and only once BOTH facts are in.
   *
   * THE DROP, NOT COMBAT. This used to wait for combat, citing a room that
   * "only accepts input during combat". The room does not: `onMessage("input")`
   * takes anything from `drop` onward, and `drop` and `combat` share one
   * `updateSimulating` arm, so the sim steps and snapshots ship throughout. The
   * client simply sat the drop out, frozen, unable to steer a fall the server
   * was already simulating — against a comment in ArenaRoom saying a player who
   * cannot steer their fall has already lost ground. What that old comment was
   * really about is WARMUP, which can run twenty seconds with input ignored;
   * starting at `drop` keeps that property.
   *
   * Colyseus sends the full state on the join ack and only then flushes the
   * messages enqueued during `onJoin` (`@colyseus/core` Room.js: `sendFullState`
   * precedes `_enqueuedMessages.forEach`). So on any join into a match already
   * past warmup — a reload mid-drop, a reconnect — the phase change arrives
   * BEFORE `world_init`, and this used to latch on it: `_spawnMoment` drew from
   * a null predictor, `reconciler?.reset()` no-opped on a reconciler that did
   * not exist yet, and the flag then blocked the real reset from ever running.
   *
   * `this.ready` is set by `_onWorldInit`, so gating on it means whichever of
   * the two arrives second does the starting.
   */
  _maybeStartPipeline(phase) {
    if (this._pipelineStarted) return;
    if (!this.ready || !isSimulatingPhase(phase)) return;
    this._pipelineStarted = true;
    this.sender.reset();
    this.reconciler?.reset();
    this.accumulatorMs = 0;
    setBootStatus('');
    boot.el.classList.add('hidden');
    this._spawnMoment();
  }

  /**
   * The ceremony runs off a SNAPSHOT, not off the phase change or `world_init`.
   *
   * It needs two things neither of those can guarantee on their own: the drop's
   * end tick (`world_init`) and where the match actually is right now
   * (`match.tick`). A snapshot is the first moment both are true, it arrives
   * within one snapshot interval of the drop opening, and it is the only
   * trigger that is also correct for someone who reloaded four seconds in.
   */
  _maybePlayDropCeremony() {
    if (this._dropCeremonyPlayed) return;
    if (session.phase !== 'drop' || session.dropEndTick <= 0) return;
    this._dropCeremonyPlayed = true;
    this._playDropCeremony();
  }

  /** Every body beams into the match instead of just being there — each in its own colour. */
  _spawnMoment() {
    if (this.predictor) {
      const p = this.predictor.player;
      playSpawnIn(_scratchA.set(p.x, p.y, p.z), session.characterId);
    }
    for (const [, mesh] of remotePlayers) playSpawnIn(mesh.position, mesh.userData.characterId ?? 'volt');
    playSpawnSound();
  }

  /**
   * Alive, in a match that is running, in a phase the server simulates — the
   * drop included, because it is a steerable fall rather than a cutscene.
   */
  _isPlaying() {
    return !this.dead && !match.ended && isSimulatingPhase(session.phase);
  }

  _clearDropTimers() {
    for (const t of this._dropTimers) clearTimeout(t);
    this._dropTimers.length = 0;
  }

  /**
   * The pre-fight beat, and the reason it moved.
   *
   * This used to be a 3-2-1-GO fired when COMBAT began — which is the moment
   * damage goes live. So the countdown ran while people were already shooting
   * each other, and "GO!" landed three seconds into a fight that had started
   * without it. The old comment admitted as much: "presentation only".
   *
   * The drop is the one stretch of a match where nobody can be hurt
   * (`applyDamage` returns 0 until `dropEndTick`), so it is the only place a
   * countdown can honestly say "not yet". The beeps are scheduled backwards
   * from the end of it, and the fight sound is the transition itself.
   */
  _playDropCeremony() {
    this._clearDropTimers();
    this.announcements.countdown('GET READY', { gold: true });
    playMatchStartHorn();

    // Server-authoritative, and measured from where the match actually IS.
    //
    // `dropEndTick` arrives in `world_init`; `match.tick` is mirrored from every
    // snapshot. Using the end tick alone would assume the drop just opened,
    // which is true on a clean start and wrong for anyone who reloaded four
    // seconds into it — they would get a countdown running past the fight.
    const remainingMs =
      session.tickHz > 0 ? ((session.dropEndTick - match.tick) / session.tickHz) * 1000 : 0;
    for (let n = 3; n >= 1; n--) {
      const at = remainingMs - n * 1000;
      // In the future, and not landing on top of GET READY. A late joiner can
      // legitimately have less than three seconds left and simply gets fewer
      // beeps rather than a burst of them at once.
      if (at < GET_READY_CLEAR_MS) continue;
      this._dropTimers.push(
        setTimeout(() => {
          this.announcements.countdown(String(n));
          // Climbing toward the fight stab, so the three beeps read as leading
          // somewhere rather than as three of the same noise.
          playCountdownBeep(440 + (3 - n) * 110);
        }, at)
      );
    }
  }

  /** Damage is live as of this frame. */
  _playFightStart() {
    this.announcements.countdown('FIGHT!', { gold: true });
    playFightSound();
  }

  _onResult(msg) {
    match.potWei = msg.potWei ?? match.potWei;
    this.hud.setPot(match.potWei);
    this.deathCam.setPot(msg.payoutWei ?? match.potWei);

    // Tell the shell to move to the results screen. A NAVIGATION HINT ONLY —
    // apps/web re-fetches the result server-side, so this cannot invent a
    // placement or a payout. Sent from `result` rather than from `match_end`
    // because settlement has run by the time `result` arrives.
    postMatchEnded(msg.matchId ?? session.matchId);
  }

  _onLeave(code) {
    this._clearDropTimers();
    clearAbilityVFX();
    clearTelegraphs();
    clearAllParticles();
    clearFx();
    clearProjectiles();
    this.loot.clear();
    this._predictedBasics.length = 0;
    endMarket(this.predictor?.world ?? null);
    session.connected = false;
    releaseAll();
    if (!match.ended) setBootStatus(`disconnected (${code})`, true);
  }

  // ── Death ───────────────────────────────────────────────────

  _enterDeathCam() {
    this.dead = true;
    releaseAll();

    // The v1 death moment (PhysicsEngine.js:70-76): audible, red, shaken — then
    // a beat on your own corpse before the death cam takes the screen. The body
    // stays visible through the hold so the elimination VFX has something to
    // happen TO.
    playDeathSound();
    screenFlash('#ff3d5a', 400);
    triggerCameraShake(0.5, 300);
    postFX.setDeathGrade?.(1);
    // The toy falls apart — the scatter plays through the hold below, so your
    // own defeat is the same fall-apart everyone else watches.
    localPlayer.mesh?.userData.driver?.die();

    // A death you come back from is not the death this screen was built for.
    //
    // `DeathCam` is the end of somebody's match — killer view, free camera, live
    // pot, requeue. In a room that respawns you it opened 700ms after you died
    // and was torn down 1.8s later by the respawn, so a practice death flashed a
    // full match-over screen and then took it away. Asked directly: "what
    // happens when i die?" — that flicker is what happened.
    //
    // The connection knows which kind of room it is; nothing else does.
    // `Connection.willRespawn` returns false, so an arena match falls through to
    // the death screen and a gym or scrim overrides it.
    if (this.connection.willRespawn()) {
      // The prompt replaces the announcement it used to push. Both said the
      // same thing; only one of them offers a way to act on it.
      if (localPlayer.mesh) localPlayer.mesh.visible = false;
      this.respawnPrompt.show();
      return;
    }

    setTimeout(() => {
      // Revived during the hold (god mode) or the match ended and showResult
      // already owns the screen.
      if (!this.dead || match.ended) return;
      // Read here, not at death: the `eliminated` event that fills _pendingDeath
      // arrives in the same batch as — but after — the snapshot that killed us.
      const info = this._pendingDeath ?? { killerId: null, killerName: null, placement: 0 };
      const me = roster.get(session.userId);
      this.deathCam.show({
        ...info,
        kills: me?.kills ?? 0,
        damageDealt: me?.damageDealt ?? 0,
      });
      if (localPlayer.mesh) localPlayer.mesh.visible = false;
    }, 700);
  }

  // ── Frame ───────────────────────────────────────────────────

  tickFrame() {
    if(this.casualSuspended)return;
    const now = performance.now();
    let delta = (now - this.lastFrameTime) / 1000;
    this.lastFrameTime = now;
    // A backgrounded tab returns with a multi-second delta. Clamping keeps one
    // stall from becoming a hundred catch-up ticks in a single frame.
    if (delta > 0.25) delta = 0.25;
    frame.delta = delta;
    frame.now = now;

    if (this.ready && this._isPlaying()) this._stepSimulation(now, delta);

    const simEnd = performance.now();
    this._upgradeLocalVisual();
    this.sceneSweep.beforeRender();
    this._render(now, delta);
    const updateEnd = performance.now();
    renderFrame(now, { simulation: simEnd - now, presentation: updateEnd - simEnd });
    if (this.ready) markBoot('firstWorldFrame');
    this.shaderWarmup.afterFrame({ worldReady: this.ready });
  }

  /**
   * The fixed-timestep half.
   *
   * Note that hitstop does NOT gate this. Freezing input sampling for 80ms would
   * send the server a gap it fills with coast frames — you would stop moving
   * every time you landed a hit.
   */
  _stepSimulation(now, delta) {
    this.accumulatorMs += delta * 1000;

    let steps = 0;
    while (this.accumulatorMs >= TICK_MS && steps < MAX_CATCHUP_TICKS) {
      this.tick += 1;
      const intent = readIntent();
      const actions = consumeActions();
      const frameOut = this.sender.sample(this.tick, this.cameraController.getYaw(), intent, actions);
      if (frameOut) {
        const predicted = this.predictor.step(frameOut);
        this.reconciler.record(frameOut);
        if (predicted.length > 0) this._onPredictedEvents(predicted);
      }
      this.accumulatorMs -= TICK_MS;
      steps += 1;
    }
    // Whatever is left after the catch-up cap is dropped rather than carried:
    // carrying it means the next frame runs the cap again and never recovers.
    if (this.accumulatorMs >= TICK_MS) this.accumulatorMs = 0;

    if (now - this.lastFlush >= INPUT_FLUSH_MS) {
      this.lastFlush = now;
      this.sender.flush();
    }
  }

  _render(now, delta) {
    const hitstopped = isHitstopped(now);
    // Presentation freezes during hitstop; everything above this line does not.
    const visualDelta = hitstopped ? 0 : delta;

    if (this.predictor) {
      const alpha = Math.min(1, this.accumulatorMs / TICK_MS);
      this.predictor.interpolate(alpha, localPlayer.renderPos);
      localPlayer.grounded = this.predictor.player.grounded;
      localPlayer.velocityY = this.predictor.player.vy;

      const mesh = localPlayer.mesh;
      if (mesh && !this.dead) {
        mesh.position.copy(localPlayer.renderPos);
        // Models are authored facing +Z; forward is (-sin yaw, -cos yaw), hence +PI.
        // Damped, not assigned: the sim updates yaw at 30Hz while the camera
        // turns every frame, so a raw assignment ratchets the body in visible
        // 33ms steps under any camera movement. 20/s is v1's follow feel
        // (PhysicsEngine.js:351 used 15/s), slightly tighter because this yaw
        // is also the aim.
        const targetYaw = this.predictor.player.yaw + Math.PI;
        mesh.rotation.y += shortAngleDist(mesh.rotation.y, targetYaw) * Math.min(1, visualDelta * 20);
        // Body scale comes from the PREDICTOR's world, the same ledger the
        // floor-mutator overlay reads, so the mesh and the hitbox the server
        // is testing against are the same size on the same tick.
        updateSquashStretch(mesh, localPlayer.velocityY, chaosPlayerScale(this.predictor.world));
        // The pose driver owns the joints: run cycle from the PREDICTOR's own
        // velocity (the same numbers the body moves by), aim from the damped
        // visual yaw — legs face the travel, torso faces the aim.
        mesh.userData.driver?.update(now, visualDelta, {
          vx: this.predictor.player.vx,
          vz: this.predictor.player.vz,
          aimYaw: mesh.rotation.y,
        });
        updateDashTrail(mesh, now);
        updateCastPose(mesh, now);
        setCharacterShimmer(mesh, localPlayer.effects.includes('invisible') ? 0.45 : 0);
        // The same call the remote path makes, so your own buffs and debuffs
        // read exactly the way your opponents' do. A status you can only see on
        // other people is half a feature. Tinted by the body's own family
        // colour so the aura and the body it wraps agree.
        setStatusAura(mesh, localPlayer.effects, mesh.userData.colorHex);
        // YOUR health, over YOUR head — the same bar everyone else wears.
        //
        // It was only on the corner HUD, which means the one number you check
        // most often lived at the far edge of the screen while your eyes were
        // on your character in the middle of it. Brawl Stars puts it over the
        // body for that reason, and it makes the fight symmetric: the bar you
        // read on an opponent and the bar you read on yourself are the same
        // object with the same colours at the same size. This is the ONLY
        // health bar on your character: a second copy pinned to the screen
        // centre (#68) doubled it, and slid off the body whenever the camera
        // leaned toward your aim.
        attachHealthBar(mesh, session.characterId);
        updateHealthBar(mesh, localPlayer.health, this.camera.position);
        // The magazine, under your own bar. Read from the PREDICTOR, so a pip
        // empties the frame you fire; the snapshot re-anchors it.
        attachAmmoPips(mesh);
        updateAmmoPips(mesh, this.predictor.player.ammo, this.predictor.reloadFraction());
        updateCoinBadge(mesh, localPlayer.coins);
        updateLeaderCrown(mesh, match.aliveCount === 2 && localPlayer.phase === 'alive');
      } else if (mesh && this.dead && mesh.userData.driver?.dead) {
        // The body is out of the fight but the toy is still falling apart —
        // the scatter physics runs through the death-cam hold.
        mesh.userData.driver.update(now, visualDelta, {});
      }

      this._updateMovementJuice(delta);
    }

    // Remote players are drawn 100ms in the past, interpolated between two
    // received snapshots. Never extrapolated — see net/snapshotBuffer.js.
    const poses = this.snapshots.sample(now);
    applyPoses(
      poses,
      Math.max(visualDelta, 1e-4),
      now,
      this._nameFor,
      this.predictor ? chaosPlayerScale(this.predictor.world) : 1
    );
    for (const [id, mesh] of remotePlayers) {
      const pose = poses.get(id);
      if (!pose) continue;
      attachHealthBar(mesh, pose.characterId);
      updateHealthBar(mesh, pose.health, this.camera.position);
      updateCoinBadge(mesh, pose.coins);
      updateLeaderCrown(mesh, match.aliveCount === 2 && pose.phase === 'alive');
    }

    this.cameraController.updateFreeMovement(delta, keys);
    this.cameraController.update(delta);
    // After the camera, because the reticle is drawn where the camera's aim ray
    // landed this frame.
    // Shown only while ACTUALLY aiming (an aimed button held, the aim stick
    // dragged, or within a beat of either) — see InputManager.isAiming.
    updateAimReticle(this.cameraController.aimPoint, getAimSlot(), this._groundY ?? 0, this.cameraController.getYaw(), isAiming(now, this.cameraController.aimStickActive()));
    this.zone.update(match.zone, now < (this._zoneWarnUntil ?? 0));
    this.loot.animate(visualDelta, now);

    updateAbilityVFX();
    // Real time, not the hitstop-frozen delta: a drawn round stands in for the
    // server's, which keeps flying through a hitstop. On the frozen clock every
    // hit left the drawn rounds further behind the real ones.
    updateProjectiles(now, delta);
    updateTelegraphs();
    updateParticles(visualDelta);
    // The kit steps on the PRESENTATION delta: hitstop holds every spark in
    // the air and every muzzle light lit, which is the frame the freeze is for.
    updateFx(visualDelta);
    updateHitFeedback();
    updateEnvironmentEffects(visualDelta, this.camera.position);
    updateConveyorScrolls(visualDelta);

    this.killFeed.update();
    this.announcements.update();
    this.deathCam.update(now);
    this._updateTension();

    if (isDebug) this._updateDebug();
  }

  /**
   * Jump chirp, landing impact, footsteps — edge-detected off the predictor
   * every render frame. v1 played all three from its physics loop; the sim is
   * shared now, so the edges are read here instead. Purely presentational.
   */
  _updateMovementJuice(delta) {
    const p = this.predictor.player;
    const playing = this._isPlaying();

    if (playing) {
      // grounded -> airborne with upward velocity: a jump. Walking off a ledge
      // leaves the ground falling and stays silent, exactly like v1.
      if (this._wasGrounded && !p.grounded && p.vy > 1) playJumpSound();

      // airborne -> grounded: the impact scales with the fastest fall speed of
      // the airtime, because the sim may have zeroed vy by the grounded frame.
      if (!this._wasGrounded && p.grounded) {
        const impact = -this._peakFallVy;
        if (impact > 12) {
          _scratchA.copy(localPlayer.renderPos);
          _scratchA.y -= PLAYER_HALF_HEIGHT; // feet, not chest
          fxLanding(_scratchA, Math.min(1, (impact - 12) / 30));
          playLandSound(Math.min(1, impact / 40));
          triggerCameraShake(Math.min(0.25, 0.1 + (impact - 12) * 0.005), 90);
        }
      }

      const hSpeed = Math.hypot(p.vx, p.vz);
      if (p.grounded && hSpeed > 2) {
        this._stepClock += delta;
        const stride = Math.min(0.5, Math.max(0.18, 5.2 / hSpeed));
        if (this._stepClock >= stride) {
          this._stepClock = 0;
          this._stepAlt = !this._stepAlt;
          playFootstep(this._stepAlt);
          // A puff of deck dust per stride — the cheapest "this body has weight".
          _scratchA.copy(localPlayer.renderPos);
          _scratchA.y -= PLAYER_HALF_HEIGHT;
          fxFootstep(_scratchA);
        }
      } else {
        // Primed so the first footfall lands just after movement starts.
        this._stepClock = 0.12;
      }
    }

    if (!p.grounded) this._peakFallVy = Math.min(this._peakFallVy, p.vy);
    else this._peakFallVy = 0;
    this._wasGrounded = p.grounded;
  }

  /**
   * Endgame tension bed: a low drone once three or fewer are standing, or the
   * zone is on its final contraction, pulsing faster as the circle closes.
   */
  _updateTension() {
    const zone = match.zone;
    const finalZone = zone.dps > 0 && zone.targetRadius <= 0.01;
    const active =
      session.connected &&
      session.phase === 'combat' &&
      !match.ended &&
      match.aliveCount >= 2 &&
      (match.aliveCount <= 3 || finalZone);

    if (!active) {
      this._drone?.stop();
      this._drone = null;
      return;
    }

    if (!this._drone) this._drone = startTensionDrone();
    let t = match.aliveCount <= 2 ? 0.6 : 0.35;
    if (finalZone) {
      const closure = 1 - zone.radius / Math.max(1, session.arenaRadius);
      t = Math.max(t, Math.min(1, 0.4 + closure * 0.6));
    }
    this._drone.setIntensity(t);
  }

  _positionOf(id, out) {
    if (!id) return null;
    if (id === session.userId) return out.copy(localPlayer.renderPos);
    const mesh = remotePlayers.get(id);
    return mesh ? out.copy(mesh.position) : null;
  }

  _updateDebug() {
    if (!this._debugEl) {
      this._debugEl = document.createElement('div');
      this._debugEl.style.cssText =
        'position:fixed;left:8px;top:8px;z-index:50;font:11px/1.5 ui-monospace,monospace;background:rgba(0,0,0,.6);padding:6px 8px;border-radius:6px;pointer-events:none;white-space:pre';
      document.body.appendChild(this._debugEl);
    }
    const s = this.reconciler?.stats ?? {};
    this._debugEl.textContent = [
      `tick ${this.tick} (srv ${match.tick})`,
      `rtt ${Math.round(this.connection.rttMs)}ms`,
      `snap age ${Math.round(this.snapshots.ageMs())}ms`,
      `err ${(s.lastError ?? 0).toFixed(3)}  corrections ${s.corrections ?? 0}`,
      `unacked ${this.sender.unacked().length}`,
      `remotes ${remotePlayers.size}`,
    ].join('\n');
  }
}

function abilityById(characterId, abilityId) {
  const def = CHARACTERS[characterId];
  if (!def) return null;
  for (const slot of ['basic', 'skill1', 'skill2', 'ultimate']) {
    if (def.abilities[slot].id === abilityId) return def.abilities[slot];
  }
  return null;
}

export function startGame(connection) {
  const game = new Game(connection);
  game.boot().catch((err) => {
    console.error('[boot]', err);
  });

  // Handy in the console; deliberately read-only in spirit — there is nothing
  // here that could change an outcome even if it were written.
  if (isDebug) window.chaos = { game, session, match, roster, isTouch, touch: touchProbe };
  return game;
}

// The gym boots the same Game itself, against a LocalConnection, and sets this
// first — otherwise importing this module would also start a second game
// against the real server.
if (!window.__chaosGymBoot) startGame();
